import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, X, Clock } from 'lucide-react';

interface TimelineEvent { id: string; year: string; title: string; description: string; category: string; createdAt: string; }
interface Props { villageId: string; villageName: string; }

const CATEGORIES = [
  { value: 'FOUNDING',    label: '🏛️ Founding' },
  { value: 'CULTURAL',    label: '🎭 Cultural' },
  { value: 'POLITICAL',   label: '⚔️ Political' },
  { value: 'DEVELOPMENT', label: '🏗️ Development' },
  { value: 'FESTIVAL',    label: '🎉 Festival' },
  { value: 'NOTABLE',     label: '⭐ Notable Event' },
];
const EMPTY_EVENT = { year: '', title: '', description: '', category: 'CULTURAL' };

const CommunityTimelineEditor: React.FC<Props> = ({ villageId, villageName }) => {
  const [events, setEvents]     = useState<TimelineEvent[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ ...EMPTY_EVENT });
  const [error, setError]       = useState('');

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const r = await fetch(`/api/community-portals/${villageId}/timeline`);
      const d = await r.json();
      if (d.success) setEvents(d.events || []);
    } catch { setError('Failed to load timeline'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchEvents(); }, [villageId]);

  const cancel = () => { setShowForm(false); setForm({ ...EMPTY_EVENT }); setError(''); };

  const save = async () => {
    if (!form.year.trim() || !form.title.trim()) { setError('Year and title are required.'); return; }
    setSaving(true); setError('');
    try {
      const r = await fetch(`/api/community-portals/${villageId}/timeline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.success) { await fetchEvents(); cancel(); } else setError(d.error || 'Save failed');
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const deleteEvent = async (id: string, title: string) => {
    if (!confirm(`Delete timeline event: "${title}"?`)) return;
    try {
      const r = await fetch(`/api/community-portals/${villageId}/timeline/${id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) await fetchEvents(); else setError(d.error || 'Delete failed');
    } catch { setError('Network error'); }
  };

  const sorted = [...events].sort((a, b) => (parseInt(a.year) || 0) - (parseInt(b.year) || 0));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Historical Timeline</h3>
          <p className="text-sm text-slate-400">{villageName} · {events.length} event{events.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setShowForm(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#D4AF37] text-slate-900 hover:bg-[#c49b2f] transition-colors">
          <Plus className="w-4 h-4" /> Add Event
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">{error}</div>}

      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-[#D4AF37]">➕ Add Timeline Event</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Year / Period *</label>
              <input value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
                placeholder="e.g. 1842 or Pre-colonial era"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-slate-300 mb-1 block">Event Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Brief title of the historical event..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-slate-300 mb-1 block">Description</label>
              <textarea rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe this historical event or period..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#0F5132] text-white hover:bg-[#0d4429] disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Add to Timeline'}
            </button>
            <button onClick={cancel}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-8 text-center">Loading timeline…</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl">
          <Clock className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No timeline events yet. Add the first historical event.</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />
          <div className="space-y-4">
            {sorted.map(ev => (
              <div key={ev.id} className="relative pl-10">
                <div className="absolute left-2.5 top-3 w-3 h-3 rounded-full bg-[#D4AF37] border-2 border-slate-900" />
                <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 hover:border-slate-600 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#D4AF37] font-bold text-sm">{ev.year}</span>
                        <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-700 rounded-full">
                          {CATEGORIES.find(c => c.value === ev.category)?.label ?? ev.category}
                        </span>
                      </div>
                      <p className="text-white font-medium text-sm">{ev.title}</p>
                      {ev.description && <p className="text-slate-400 text-xs mt-1 line-clamp-3">{ev.description}</p>}
                    </div>
                    <button onClick={() => deleteEvent(ev.id, ev.title)}
                      className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-700 transition-colors flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default CommunityTimelineEditor;
